import React, { useState, useRef, useEffect } from 'react';
import UserAvatar from './UserAvatar';
import { filterAssignableUsers, formatUserLabel } from '../utils/companyUsers';
import './UserAssignPicker.css';

const INITIAL_LIMIT = 5;

const UserAssignPicker = ({
  users = [],
  selectedUsers = [],
  onChange,
  onSelect,
  excludeIds = [],
  placeholder = 'Search by name or email…',
  multi = true
}) => {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const containerRef = useRef(null);

  const excluded = multi
    ? [...excludeIds, ...selectedUsers.map((user) => user.id)]
    : excludeIds;

  const filteredUsers = filterAssignableUsers(users, { excludeIds: excluded, search });
  const suggestions = showAll ? filteredUsers : filteredUsers.slice(0, INITIAL_LIMIT);
  const hasMore = filteredUsers.length > INITIAL_LIMIT;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
        setShowAll(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (user) => {
    if (multi && onChange) {
      onChange([...selectedUsers, user]);
    } else if (onSelect) {
      onSelect(user);
    }
    setSearch('');
    setOpen(false);
    setShowAll(false);
  };

  const handleRemove = (userId) => {
    if (onChange) {
      onChange(selectedUsers.filter((user) => user.id !== userId));
    }
  };

  return (
    <div className="user-assign-picker" ref={containerRef}>
      {multi && selectedUsers.length > 0 && (
        <div className="user-assign-chips">
          {selectedUsers.map((user) => (
            <span key={user.id} className="user-assign-chip">
              <UserAvatar user={user} size={24} />
              <span className="user-assign-chip-label">{formatUserLabel(user)}</span>
              <button
                type="button"
                className="user-assign-chip-remove"
                onClick={() => handleRemove(user.id)}
                aria-label={`Remove ${formatUserLabel(user)}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      <input
        type="text"
        className="user-assign-input"
        value={search}
        onChange={(event) => {
          setSearch(event.target.value);
          setShowAll(false);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        autoComplete="off"
      />
      {open && suggestions.length > 0 && (
        <div className="user-assign-dropdown">
          <ul className="user-assign-suggestions" role="listbox">
            {suggestions.map((user) => (
              <li
                key={user.id}
                role="option"
                onMouseDown={(event) => {
                  event.preventDefault();
                  handleSelect(user);
                }}
              >
                <UserAvatar user={user} size={28} />
                <span className="user-assign-suggestion-label">{formatUserLabel(user)}</span>
                {user.role && <span className="user-assign-role">{user.role}</span>}
              </li>
            ))}
          </ul>
          {hasMore && !showAll && (
            <button
              type="button"
              className="user-assign-show-more"
              onMouseDown={(event) => {
                event.preventDefault();
                setShowAll(true);
              }}
            >
              Show more ({filteredUsers.length - INITIAL_LIMIT} more)
            </button>
          )}
        </div>
      )}
      {open && search.trim() && suggestions.length === 0 && (
        <div className="user-assign-empty">No matching users</div>
      )}
    </div>
  );
};

export default UserAssignPicker;
